import { action, observable, makeObservable } from 'mobx';
import moment from 'moment';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as AttrUtils from 'Util/AttributeUtils';

export interface IPassengerEntityAttributes extends IModelAttributes {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	gender: string;
	dateOfBirth: Date;
	address: string;
	age: number;
	removed: boolean;

	passengerDetails: Array<Models.PassengerDetailPassenger | Models.IPassengerDetailPassengerAttributes>;
	userId: string;
	user: Models.UserEntity | Models.IUserEntityAttributes;
	driver: boolean;
}
@entity('PassengerEntity', 'Passenger')
export default class PassengerEntity extends Model implements IPassengerEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The first name of the passenger.
	 */
	@observable
	@attribute<PassengerEntity, string>()
	public firstName: string;

	/**
	 * The last name of the passenger.
	 */
	@observable
	@attribute<PassengerEntity, string>()
	public lastName: string;

	/**
	 * The contact email for the passenger.
	 */
	@observable
	@attribute<PassengerEntity, string>()
	public email: string;

	/**
	 * The contact phone for the passenger.
	 */
	@observable
	@attribute<PassengerEntity, string>()
	public phone: string;

	/**
	 * The gender of the passenger.
	 */
	@observable
	@attribute<PassengerEntity, string>()
	public gender: string;

	/**
	 * The date of birth of the passenger.
	 */
	@observable
	@attribute<PassengerEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	public dateOfBirth: Date;

	/**
	 * The address of the passenger.
	 */
	@observable
	@attribute<PassengerEntity, string>()
	public address: string;

	/**
	 * The age of the passenger.
	 */
	@observable
	@attribute<PassengerEntity, number>()
	public age: number;

	@observable
	@attribute<PassengerEntity, boolean>()
	public removed: boolean;

	@observable
	@attribute<PassengerEntity, boolean>()
	public driver: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public passengerDetails: Models.PassengerDetailPassenger[] = [];

	@observable
	@attribute()
	public userId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	constructor(attributes?: Partial<IPassengerEntityAttributes>) {
		super(attributes);

		this.passengerDetails = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IPassengerEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.firstName !== undefined) {
				this.firstName = attributes.firstName;
			}
			if (attributes.lastName !== undefined) {
				this.lastName = attributes.lastName;
			}
			if (attributes.email !== undefined) {
				this.email = attributes.email;
			}
			if (attributes.phone !== undefined) {
				this.phone = attributes.phone;
			}
			if (attributes.gender !== undefined) {
				this.gender = attributes.gender;
			}
			if (attributes.dateOfBirth !== undefined) {
				if (attributes.dateOfBirth === null) {
					this.dateOfBirth = attributes.dateOfBirth;
				} else {
					this.dateOfBirth = moment(attributes.dateOfBirth).toDate();
				}
			}
			if (attributes.address !== undefined) {
				this.address = attributes.address;
			}
			if (attributes.age !== undefined) {
				this.age = attributes.age;
			}
			if (attributes.removed !== undefined) {
				this.removed = attributes.removed;
			}
			if (attributes.driver !== undefined) {
				this.driver = attributes.driver;
			}
			if (attributes.passengerDetails !== undefined && Array.isArray(attributes.passengerDetails)) {
				if (!this.passengerDetails) {
					this.passengerDetails = [];
				}
				for (const model of attributes.passengerDetails) {
					if (model instanceof Models.PassengerDetailPassenger) {
						this.passengerDetails.push(model);
					} else {
						this.passengerDetails.push(new Models.PassengerDetailPassenger(model));
					}
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
		}
	}
}
